import { useEffect, useState, type FocusEvent, type KeyboardEvent, type Ref } from "react";
import type { MunicipalityMatch } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Combobox, type ComboboxOption } from "../../design/components/Combobox";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { municipalitiesQuery } from "../lead-queries";

// Il selettore della città (piano M2 Task 17 per «Nuovo Lead», poi la scheda del Task 18): si scrive soltanto la città,
// anche una parte e senza accenti, e si sceglie fra al massimo otto comuni, cercati dal server (il catalogo ISTAT non
// entra nel browser); provincia, regione e nazione si compilano da sole (decisione del 25 settembre 2026). È una
// casella combinata ARIA (`Combobox`): le frecce scorrono i comuni, Invio sceglie, Esc chiude l'elenco senza chiudere la finestra.
// Sulla scheda sta dentro la riga «Città», che ha già il suo nome: lì l'etichetta resta per gli screen reader e non si
// vede, e i tasti che l'elenco non usa (Invio senza comuni, Esc a elenco chiuso) arrivano alla riga.

/** Ciò che la casella contiene: il testo scritto e, se è stato scelto, il comune. */
export type CityValue = { text: string; match: MunicipalityMatch | null };

export const EMPTY_CITY: CityValue = { text: "", match: null };

/** Sotto i due caratteri il server non cerca. */
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 150;

type Props = {
  id: string;
  tab: TabContext;
  value: CityValue;
  onChange: (value: CityValue) => void;
  error?: string;
  /** Arriva alla casella, per riportarvi il focus dopo un errore. */
  ref?: Ref<HTMLInputElement>;
  /** L'etichetta resta per gli screen reader ma non si vede (la riga «Città» della scheda). */
  hideLabel?: boolean;
  /** I tasti che l'elenco non usa. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Un'altra descrizione della casella, dopo la riga di provincia e regione (i tasti della riga sulla scheda). */
  describedBy?: string;
};

export function CityPicker({ id, tab, value, onChange, error, ref, hideLabel, onKeyDown, onBlur, describedBy }: Props) {
  const t = useT();
  const typed = value.match ? "" : value.text.trim();
  const [query, setQuery] = useState(typed);

  // Si cerca 150 ms dopo l'ultimo tasto, e soltanto finché non si è scelto un comune.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const searching = query.length >= MIN_SEARCH_LENGTH && value.match === null;
  const results = useQuery({ ...municipalitiesQuery(tab, query), enabled: searching });
  const matches = searching ? (results.data ?? []) : [];
  // L'elenco si apre soltanto mentre si scrive una città da cercare (l'apertura la tiene la casella).
  const listed = typed.length >= MIN_SEARCH_LENGTH && value.match === null;
  const placeOf = (match: MunicipalityMatch) =>
    t("cityPicker.place", { province: match.provinceName, region: match.regionName, country: t("cityPicker.italy") });
  const options: ComboboxOption[] = listed ? matches.map((match) => ({ id: match.istatCode, label: match.label, detail: placeOf(match) })) : [];

  const place = value.match ? placeOf(value.match) : t("cityPicker.hint");
  let status: string | null = null;
  if (listed && (results.isFetching || query !== typed)) status = t("cityPicker.searching");
  else if (listed && matches.length === 0 && results.isSuccess) status = t("cityPicker.none", { q: typed });

  return (
    <Combobox
      ref={ref}
      id={id}
      className="g-city-picker"
      label={t("cityPicker.label")}
      hideLabel={hideLabel}
      maxLength={100}
      value={value.text}
      placeholder={t("cityPicker.placeholder")}
      onTextChange={(text) => onChange({ text, match: null })}
      options={options}
      onPick={(option) => {
        const match = matches.find((candidate) => candidate.istatCode === option.id);
        if (match) onChange({ text: match.label, match });
      }}
      listLabel={t("cityPicker.results")}
      status={status}
      hint={place}
      error={error}
      describedBy={describedBy}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    />
  );
}
